import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { FieldsetModule } from 'primeng/fieldset';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { StepperModule } from 'primeng/stepper';
import { DialogModule } from 'primeng/dialog';
import { ProjectService } from '../../services/project.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { OracleprojectService } from '../../services/oracleproject.service';
import { CountryModel, ProjectModel } from '../../models/project.model';
import { BusinessUnitModel } from '../../models/template.model';
import { TemplateService } from '../../services/template.service';
import { forkJoin, Subject, debounceTime, distinctUntilChanged, takeUntil, finalize, switchMap, throwError, of } from 'rxjs';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';
import { environment } from '../../../environments/environment';

interface LoqateAddressMatch {
  AQI?: string;
  Address?: string;
  Address1?: string;
  Address2?: string;
  AdministrativeArea?: string;
  Country?: string;
  CountryName?: string;
  GeoAccuracy?: string;
  'ISO3166-2'?: string;
  'ISO3166-3'?: string;
  Latitude?: string;
  Locality?: string;
  Longitude?: string;
  PostalCode?: string;
  PostalCodePrimary?: string;
  DeliveryAddress?: string;
  DeliveryAddress1?: string;
  DeliveryAddress2?: string;
}

@Component({
  selector: 'app-project',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    CardModule,
    DatePickerModule,
    SelectModule,
    FloatLabelModule,
    InputNumberModule,
    FieldsetModule,
    ProgressSpinnerModule,
    StepperModule,
    ToastModule,
    DialogModule
  ],
  providers: [MessageService],
  templateUrl: './project.html',
  styleUrls: ['./project.css'],
})
export class ProjectComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('projectNumberInput') projectNumberInput!: ElementRef;
  projectId: number = 0;
  projectForm!: FormGroup;
  submitted = false;
  isSaving = false;
  isRefreshingOracleProject = false;
  isLoadingDropdowns = false;
  isProjectNumberVerified = false;
  projectNumberVerificationStatus: 'none' | 'verifying' | 'verified' | 'failed' = 'none';
  projectNumberErrorMessage: string = '';
  skidConfigurationError = false;
  private destroy$ = new Subject<void>();
  private isPatchingAddressFromLoqate = false;
  private isInitializingProjectForm = true;
  private lastLoqateRequestSignature = '';
  private lastLoqateWarningSignature = '';

  // Dropdown options - initialize with empty arrays to prevent undefined issues
  inverterModelOptions: any[] = [];
  transformerOptions: any[] = [];
  incoTermsOptions: any[] = [];
  integratorOptions: any[] = [];
  projectStatusOptions: any[] = [];
  countryOptions: CountryModel[] = [];
  loqateMatches: LoqateAddressMatch[] = [];
  showLoqateMatchDialog = false;
  isVerifyingAddress = false;

  // Static options as readonly to prevent accidental modification
  readonly projectTypeOptions = ['PV', 'ESS'];
  readonly auxPackageOptions = ['7.5 kVA', '10 kVA', '35 kVA', '55 kVA'];
  readonly oilContainmentOptions = ['Yes', 'No'];
  readonly auxiliaryRackLightOptions = ['0', '1', '2'];
  readonly cfgiOptions = ['1', '2'];
  businessUnitOptions: BusinessUnitModel[] = [];
  activeStep = 1;
  private createSuccessResult: any = null;
  constructor(
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private _service: ProjectService,
    private _templateService: TemplateService,
    private _oracleService: OracleprojectService,
    private messageService: MessageService
  ) { }

  // Getter for form controls
  get f() { return this.projectForm?.controls || {}; }

  // Getter for create button disabled state
  get isCreateButtonDisabled(): boolean {
    if (this.isCreateMode && this.activeStep === 1) {
      return !this.canProceedToDetails || this.isSaving;
    }

    if (this.isCreateMode && this.activeStep === 3) {
      return false;
    }

    if (this.projectId > 0) {
      return this.projectForm.invalid ||
        this.isSaving ||
        this.isRefreshingOracleProject;
    }
    return this.projectForm.invalid ||
      !this.isProjectNumberVerified ||
      this.isSaving ||
      this.isRefreshingOracleProject ||
      this.projectNumberVerificationStatus === 'verifying';
  }

  get isCreateMode(): boolean {
    return this.projectId === 0;
  }

  get selectedBusinessUnitName(): string {
    const selectedId = this.f['businessUnitId']?.value;
    const unit = this.businessUnitOptions.find(x => String(x.id) === String(selectedId));
    return (unit?.name || '').trim();
  }

  get isRESBusinessUnit(): boolean {
    return this.getBusinessUnitMode() === 'RES';
  }

  get isGIBusinessUnit(): boolean {
    return this.getBusinessUnitMode() === 'GI';
  }

  get isOthersBusinessUnit(): boolean {
    return this.getBusinessUnitMode() === 'OTHERS';
  }

  get canProceedToDetails(): boolean {
    return !!this.f['businessUnitId']?.value && !this.isOthersBusinessUnit;
  }

  private normalizeBusinessUnit(value: string): string {
    return (value || '').trim().toUpperCase();
  }

  private getBusinessUnitMode(): 'RES' | 'GI' | 'OTHERS' {
    const normalized = this.normalizeBusinessUnit(this.selectedBusinessUnitName);

    if (normalized === 'OTHERS' || normalized === 'OTHER') return 'OTHERS';
    if (normalized === 'GI' || normalized.startsWith('GI ') || normalized.includes('(GI)')) return 'GI';
    if (normalized === 'RES' || normalized.startsWith('RES ') || normalized.includes('(RES)')) return 'RES';

    // Treat unknown/legacy BU names as RES to preserve existing project creation behavior.
    return 'RES';
  }

  // Custom validator for skid configuration
  private skidConfigurationValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent) {
        return null;
      }

      const n1 = control.parent.get('n1')?.value || 0;
      const n2 = control.parent.get('n2')?.value || 0;
      const n3 = control.parent.get('n3')?.value || 0;
      const n4 = control.parent.get('n4')?.value || 0;
      const n5 = control.parent.get('n5')?.value || 0;
      const n6 = control.parent.get('n6')?.value || 0;

      const totalSkids = Number(n1) + Number(n2) + Number(n3) + Number(n4) + Number(n5) + Number(n6);

      if (totalSkids <= 0) {
        this.skidConfigurationError = true;
        return { skidConfigurationRequired: true };
      }

      this.skidConfigurationError = false;
      return null;
    };
  }

  ngOnInit() {
    this.projectId = this.config.data?.projectId || 0;
    console.log('Project dialog initialized with projectId:', this.projectId);

    // CRITICAL: Always initialize form synchronously first
    this.initForm({ projectId: this.projectId } as ProjectModel);

    // Then load data for edit mode
    if (this.projectId > 0) {
      this._service.getProjectById(this.projectId).subscribe({
        next: (projectData) => {
          console.log('Loaded project data for editing:', projectData);
          // Convert date strings to Date objects for DatePicker components
          if (projectData.startDate) {
            projectData.startDate = new Date(projectData.startDate);
          }
          if (projectData.deliveryDate) {
            projectData.deliveryDate = new Date(projectData.deliveryDate);
          }
          if (projectData.cxStartDate) {
            projectData.cxStartDate = new Date(projectData.cxStartDate);
          }
          if (projectData.backfeedDate) {
            projectData.backfeedDate = new Date(projectData.backfeedDate);
          }
          // Patch values instead of re-initializing
          this.projectForm.patchValue(projectData, { emitEvent: false });
          this.setDefaultCountryIfMissing();
          this.applyBusinessUnitRules();
          this.isInitializingProjectForm = false;
          this.cdr.markForCheck();
        },
      });
    } else {
      // Setup defaults only for create mode
      this.setupFormDefaults();
      this.isInitializingProjectForm = false;
    }

    this.loadDropdownData();
    this.setupProjectNumberValidation();
    this.setupAddressGeocoding();

    if (this.isCreateMode) {
      this.f['businessUnitId']?.valueChanges.pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.applyBusinessUnitRules();
        if (this.activeStep === 2 && !this.canProceedToDetails) {
          this.activeStep = 1;
        }
        this.cdr.markForCheck();
      });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupAddressGeocoding() {
    const addressFields = ['customerAddress1', 'customerAddress2', 'customerCity', 'customerState', 'countryId', 'customerZip'];

    addressFields.forEach(field => {
      this.f[field]?.valueChanges.pipe(
        debounceTime(700),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      ).subscribe(() => {
        void this.verifyAddressWithLoqate();
      });
    });
  }

  private getSelectedCountry(): CountryModel | null {
    const countryId = Number(this.f['countryId']?.value);
    if (!Number.isFinite(countryId) || countryId <= 0) {
      return null;
    }

    return this.countryOptions.find(country => country.countryId === countryId) || null;
  }

  private setDefaultCountryIfMissing(): void {
    if (!this.projectForm || this.f['countryId']?.value || !this.countryOptions.length) {
      return;
    }

    const usCountry = this.countryOptions.find(country =>
      String(country.iso2Code || '').trim().toUpperCase() === 'US'
    );

    if (usCountry) {
      this.f['countryId']?.setValue(usCountry.countryId, { emitEvent: false });
    }
  }

  private buildLoqateRequestSignature(): string {
    return [
      this.f['customerAddress1']?.value,
      this.f['customerAddress2']?.value,
      this.f['customerCity']?.value,
      this.f['customerState']?.value,
      this.f['countryId']?.value,
      this.f['customerZip']?.value
    ].map(value => String(value || '').trim()).join('|');
  }

  private hasRequiredLoqateAddressFields(): boolean {
    return !!String(this.f['customerAddress1']?.value || '').trim()
      && !!String(this.f['customerCity']?.value || '').trim()
      && !!String(this.f['customerState']?.value || '').trim()
      && !!this.getSelectedCountry()?.iso2Code;
  }

  private async verifyAddressWithLoqate(): Promise<void> {
    if (this.isInitializingProjectForm || this.isPatchingAddressFromLoqate || !this.hasRequiredLoqateAddressFields()) {
      return;
    }

    const signature = this.buildLoqateRequestSignature();
    if (!signature || signature === this.lastLoqateRequestSignature) {
      return;
    }

    const country = this.getSelectedCountry();
    if (!country?.iso2Code || !environment.loqateKey) {
      return;
    }

    this.lastLoqateRequestSignature = signature;
    this.isVerifyingAddress = true;

    try {
      const response = await fetch(`https://api.addressy.com/Cleansing/International/Batch/v1.20/json6.ws?Key=${encodeURIComponent(environment.loqateKey)}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          Key: environment.loqateKey,
          Geocode: true,
          Addresses: [
            {
              Address1: String(this.f['customerAddress1']?.value || '').trim(),
              Address2: String(this.f['customerAddress2']?.value || '').trim(),
              Address3: '',
              Address4: '',
              Locality: String(this.f['customerCity']?.value || '').trim(),
              AdministrativeArea: String(this.f['customerState']?.value || '').trim(),
              Country: country.iso2Code,
              PostalCode: String(this.f['customerZip']?.value || '').trim()
            }
          ]
        })
      });

      if (!response.ok) {
        throw new Error(`Loqate request failed with status ${response.status}`);
      }

      const data = await response.json();
      const matches = Array.isArray(data) ? (data[0]?.Matches || []) : [];
      if (!matches.length) {
        this.showLoqateWarningOnce(signature, 'No verified address matches were found. You can continue with the entered address.');
        return;
      }

      this.loqateMatches = matches;
      this.showLoqateMatchDialog = true;
    } catch {
      this.showLoqateWarningOnce(signature, 'Address verification is unavailable. You can continue with the entered address.');
    } finally {
      this.isVerifyingAddress = false;
      this.cdr.markForCheck();
    }
  }

  private showLoqateWarningOnce(signature: string, detail: string): void {
    if (this.lastLoqateWarningSignature === signature) {
      return;
    }

    this.lastLoqateWarningSignature = signature;
    this.messageService.add({ severity: 'warn', summary: 'Address Verification', detail });
  }

  getLoqateMatchDisplay(match: LoqateAddressMatch): string {
    return (match.Address || [
      match.Address1 || match.DeliveryAddress1 || match.DeliveryAddress,
      [match.Locality, match.AdministrativeArea, match.PostalCode].filter(Boolean).join(' '),
      match.CountryName
    ].filter(Boolean).join('<br>')).replace(/<br\s*\/?>/gi, ', ');
  }

  private resolveCountryFromLoqateMatch(match: LoqateAddressMatch): CountryModel | null {
    const normalize = (value: unknown) => String(value || '').trim().toUpperCase();
    const countryCodeCandidates = [
      normalize(match.Country),
      normalize(match['ISO3166-2']),
      normalize(match['ISO3166-3']).slice(0, 2)
    ].filter(Boolean);

    const byCode = this.countryOptions.find(option =>
      countryCodeCandidates.includes(normalize(option.iso2Code))
    );

    if (byCode) {
      return byCode;
    }

    const countryName = normalize(match.CountryName);
    if (!countryName) {
      return null;
    }

    return this.countryOptions.find(option => normalize(option.countryName) === countryName) || null;
  }

  selectLoqateMatch(match: LoqateAddressMatch): void {
    const country = this.resolveCountryFromLoqateMatch(match);

    this.isPatchingAddressFromLoqate = true;
    this.projectForm.patchValue({
      customerAddress1: match.DeliveryAddress1 ?? '',
      customerAddress2: match.DeliveryAddress2 ?? '',
      customerCity: match.Locality ?? '',
      customerState: match.AdministrativeArea ?? '',
      customerZip: match.PostalCodePrimary ?? '',
      latitude: match.Latitude ?? '',
      longitude: match.Longitude ?? ''
    }, { emitEvent: false });
    if (country) {
      this.f['countryId']?.setValue(country.countryId, { emitEvent: false });
      this.f['countryId']?.markAsDirty();
      this.f['countryId']?.updateValueAndValidity({ emitEvent: false });
    }

    this.lastLoqateRequestSignature = this.buildLoqateRequestSignature();
    this.showLoqateMatchDialog = false;
    this.loqateMatches = [];
    this.isPatchingAddressFromLoqate = false;
    this.cdr.markForCheck();
  }

  private setupFormDefaults() {
    // Set default values only if form exists and fields are empty
    const defaultMappings = [
      { field: 'projectType', options: this.projectTypeOptions },
      { field: 'auxPackage', options: this.auxPackageOptions },
      { field: 'oilContainment', options: this.oilContainmentOptions },
      { field: 'auxiliaryRackLight', options: this.auxiliaryRackLightOptions },
      { field: 'numberGfci', options: this.cfgiOptions }
    ];

    defaultMappings.forEach(({ field, options }) => {
      const control = this.f[field];
      if (control && !control.value && options.length > 0) {
        control.setValue(options[0], { emitEvent: false });
      }
    });
  }

  private setupProjectNumberValidation() {
    // Safety check: ensure form and control exist
    if (!this.projectForm || !this.f['projectNumber']) {
      console.error('Form not initialized before validation setup');
      return;
    }

    // Setup project number validation on value changes for clearing verification status
    this.f['projectNumber'].valueChanges.pipe(
      distinctUntilChanged(), // Only trigger when value actually changes
      takeUntil(this.destroy$)
    ).subscribe(projectNumber => {
      // Reset verification status when user types (but don't validate yet)
      if (this.projectNumberVerificationStatus !== 'none') {
        if (!projectNumber || !projectNumber.trim()) {
          this.resetProjectNumberVerification();
        } else if (this.projectNumberVerificationStatus === 'verified' || this.projectNumberVerificationStatus === 'failed') {
          // Reset status if user changes a previously validated number
          this.projectNumberVerificationStatus = 'none';
          this.isProjectNumberVerified = false;
          this.clearOraclePopulatedFields();
          this.cdr.markForCheck();
        }
      }
    });
  }

  // Method to be called on blur event from template
  onProjectNumberBlur() {
    const projectNumber = this.f['projectNumber']?.value;
    if (projectNumber && projectNumber.trim()) {
      this.validateProjectNumber(projectNumber.trim());
    } else {
      this.resetProjectNumberVerification();
    }
  }

  private validateProjectNumber(projectNumber: string) {
    this.projectNumberVerificationStatus = 'verifying';
    this.isProjectNumberVerified = false;
    this.projectNumberErrorMessage = '';
    this.cdr.markForCheck();

    // STEP 1: Check if project exists in local Kansei database first
    this._service.checkProjectNumberExists(projectNumber).pipe(
      switchMap(localResult => {
        if (localResult.exists && localResult.project) {
          // Project already exists in Kansei database
          return throwError(() => ({
            type: 'PROJECT_EXISTS_LOCALLY',
            project: localResult.project
          }));
        }
        // STEP 2: If not in local DB, check Oracle EBS database
        return this._oracleService.getOracleProjectList({ projectNumber: projectNumber });
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (oracleData) => {
        // Found in Oracle EBS, populate form fields
        if (oracleData) {
          this.projectNumberVerificationStatus = 'verified';
          this.isProjectNumberVerified = true;
          this.projectNumberErrorMessage = '';
          this.populateOracleData(oracleData);
          this.cdr.markForCheck();
        } else {
          this.projectNumberVerificationStatus = 'failed';
          this.isProjectNumberVerified = false;
          this.projectNumberErrorMessage = `Project number "${projectNumber}" not found in Oracle EBS. Please verify the project number.`;
          this.handleLoadingError(this.projectNumberErrorMessage);
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        this.projectNumberVerificationStatus = 'failed';
        this.isProjectNumberVerified = false;

        if (error.type === 'PROJECT_EXISTS_LOCALLY') {
          // Project already exists in Kansei database
          const existingProject = error.project;
          this.projectNumberErrorMessage = `Project number "${projectNumber}" already exists in Navigator.`;
          this.handleLoadingError(
            `Project number "${projectNumber}" already exists in Navigator as "${existingProject.projectName}". ` +
            `Please use a different project number or edit the existing project.`
          );
        } else {
          // Network error or Oracle EBS error
          console.error('Error validating project number:', error);
          this.projectNumberErrorMessage = 'Failed to validate project number. Please try again.';
          this.handleLoadingError(this.projectNumberErrorMessage);
        }
        this.cdr.markForCheck();
      }
    });
  }

  refreshOracleProjectData(): void {
    if (this.projectId <= 0 || this.isRefreshingOracleProject || this.isSaving) {
      return;
    }

    const projectNumber = String(this.f['projectNumber']?.value || '').trim();
    if (!projectNumber) {
      this.handleLoadingError('Project number is required to refresh Oracle project data.');
      return;
    }

    this.isRefreshingOracleProject = true;
    this.cdr.markForCheck();

    this._oracleService.getOracleProjectList({ projectNumber }).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isRefreshingOracleProject = false;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (oracleData) => {
        if (!oracleData) {
          this.handleLoadingError(`Project number "${projectNumber}" not found in Oracle EBS.`);
          return;
        }

        this.populateOracleData(oracleData);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Project data refreshed from Oracle. Click Save Changes to apply it.'
        });
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error refreshing Oracle project data:', error);
        this.handleLoadingError('Failed to refresh project data from Oracle. Please try again.');
      }
    });
  }

  private resetProjectNumberVerification() {
    this.projectNumberVerificationStatus = 'none';
    this.isProjectNumberVerified = false;
    this.projectNumberErrorMessage = '';
    // Clear form fields that were populated from Oracle
    this.clearOraclePopulatedFields();
    this.cdr.markForCheck();
  }

  private populateOracleData(oracleProject: any) {
    this.projectForm.patchValue({
      projectName: oracleProject.name || '',
      projectManager: oracleProject.projectManager || '',
      engineer: oracleProject.projectEngineer || '',
      projectCoordinator: oracleProject.projectCoordinator || '',
      commProject: oracleProject.commProject || '',
      warrantyProject: oracleProject.warrentyProject || '', // Note: API returns 'warrentyProject' (typo in API)
      customerName: oracleProject.customerName || '',
      endUserName: oracleProject.endUserName || ''
    });
  }

  private clearOraclePopulatedFields() {
    this.projectForm.patchValue({
      projectName: '',
      projectManager: '',
      engineer: '',
      projectCoordinator: '',
      commProject: '',
      warrantyProject: '',
      customerName: '',
      endUserName: ''
    });
  }

  ngAfterViewInit() {
    // Set focus to the first input field (Project Number) after view initialization
    setTimeout(() => {
      if (this.projectNumberInput) {
        this.projectNumberInput.nativeElement.focus();
      }
    }, 100);
  }

  private loadDropdownData() {
    this.isLoadingDropdowns = true;

    // Load all dropdown data in parallel using forkJoin for better performance
    const dropdownCalls = forkJoin({
      inverterModels: this._service.getInverterModelList(),
      transformers: this._service.getTransformerList(),
      incoterms: this._service.getIncotermList(),
      integrators: this._service.getIntegratorList(),
      projectStatuses: this._service.getBsProjectStatusList(),
      countries: this._service.getCountryList(),
      businessUnits: this._templateService.getBusinessUnits()
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isLoadingDropdowns = false;
      })
    );

    dropdownCalls.subscribe({
      next: (data) => {
        // Defer assignment to next change detection cycle
        setTimeout(() => {
          // Assign data to component properties
          this.inverterModelOptions = data.inverterModels || [];
          this.transformerOptions = data.transformers || [];
          this.incoTermsOptions = data.incoterms || [];
          this.integratorOptions = data.integrators || [];
          this.projectStatusOptions = data.projectStatuses || [];
          this.countryOptions = data.countries || [];
          this.setDefaultCountryIfMissing();
          this.businessUnitOptions = (data.businessUnits || []).map(x => ({
            ...x,
            displayName: x.displayName || x.name
          }));

          console.log('All dropdown data loaded successfully');

          // Set default values for dropdown fields
          this.setDropdownDefaults(data);
          this.applyBusinessUnitRules();
          this.cdr.markForCheck();
        }, 0);
      },
      error: (error) => {
        console.error('Error loading dropdown data:', error);
        this.handleLoadingError('Failed to load dropdown options. Please refresh the page.');
      }
    });
  }

  private setDropdownDefaults(data: any) {
    const dropdownMappings = [
      { field: 'inverterModel', data: data.inverterModels, idField: 'inverterModelId' },
      { field: 'transformer', data: data.transformers, idField: 'transformerId' },
      { field: 'incoTerms', data: data.incoterms, idField: 'incotermId' },
      { field: 'integrator', data: data.integrators, idField: 'integratorId' },
      { field: 'projectStatus', data: data.projectStatuses, idField: 'projectStatusId' }
    ];

    dropdownMappings.forEach(({ field, data: items, idField }) => {
      const control = this.f[field];
      if (control && !control.value && items && items.length > 0) {
        control.setValue(items[0][idField] || null, { emitEvent: false });
      }
    });
  }

  private handleLoadingError(message: string) {
    console.log(message);
    // You can add toast notifications or other error handling here
    this.messageService.add({ severity: 'error', summary: 'Error', detail: message });
  }

  initForm(item: ProjectModel) {
    const businessUnitValidators = this.projectId > 0 ? [] : [Validators.required];

    this.projectForm = this.fb.group({
      businessUnitId: [null, businessUnitValidators],
      projectId: [item.projectId || 0],
      projectNumber: [item.projectNumber || '', [Validators.required, Validators.maxLength(50)]],
      projectName: [item.projectName || '', [Validators.required, Validators.maxLength(50)]],
      commProject: [item.commProject || ''],
      warrantyProject: [item.warrantyProject || ''],
      projectManager: [item.projectManager || ''],
      engineer: [item.engineer || ''],
      projectCoordinator: [item.projectCoordinator || ''],
      projectType: [item.projectType || null, Validators.required], // Start with null to prevent auto-selection
      projectStatusId: [item.projectStatusId || null, Validators.required], // Start with null to prevent auto-selection
      inverterModelId: [item.inverterModelId || null, Validators.required], // Start with null to prevent auto-selection
      customerName: [item.customerName || ''],
      endUserName: [item.endUserName || '', Validators.maxLength(100)],
      siteContact: [item.siteContact || '', Validators.maxLength(100)],
      customerContactNumber: [item.customerContactNumber || '', Validators.maxLength(50)],
      customerEmailAddress: [item.customerEmailAddress || '', [Validators.email, Validators.maxLength(200)]],
      customerAddress1: [item.customerAddress1 || '', Validators.maxLength(100)],
      customerAddress2: [item.customerAddress2 || '', Validators.maxLength(100)],
      customerCity: [item.customerCity || '', [Validators.required, Validators.maxLength(50)]],
      customerState: [item.customerState || '', [Validators.required, Validators.maxLength(20)]],
      countryId: [item.countryId ?? null],
      customerZip: [item.customerZip || '', Validators.maxLength(10)],
      notes: [item.notes || '', Validators.maxLength(500)],
      transformerId: [item.transformerId || null], // Start with null to prevent auto-selection
      integratorId: [item.integratorId || null], // Start with null to prevent auto-selection
      startDate: [item.startDate || null, Validators.required],
      deliveryDate: [item.deliveryDate || null, Validators.required],
      cxStartDate: [item.cxStartDate || null],
      n1: [item.n1 || null],
      n2: [item.n2 || null],
      n3: [item.n3 || null],
      n4: [item.n4 || null],
      n5: [item.n5 || null],
      n6: [item.n6 || null],
      n7: [item.n7 || null],
      skids: [item.skids || null, this.skidConfigurationValidator()],
      auxPackage: [item.auxPackage || null, Validators.required], // Start with null to prevent auto-selection
      oilContainment: [item.oilContainment || null, Validators.required], // Start with null to prevent auto-selection
      auxiliaryRackLight: [item.auxiliaryRackLight || null], // Start with null to prevent auto-selection
      numberGfci: [item.numberGfci || null],
      backfeedDate: [item.backfeedDate || null],
      incoTermsId: [item.incoTermsId || null, Validators.required], // Start with null to prevent auto-selection
      freightBroker: [item.freightBroker || ''],
      isActive: [item.isActive !== undefined ? item.isActive : 1], // Add isActive to form with default value
      prefix: [item.prefix || '', Validators.maxLength(10)],
      latitude: [item.latitude ?? null],
      longitude: [item.longitude ?? null]
    });

  }

  goToDetailsStep(): void {
    if (!this.isCreateMode || !this.canProceedToDetails) return;
    this.applyBusinessUnitRules();
    this.activeStep = 2;
    this.cdr.markForCheck();
  }

  goToBusinessUnitStep(): void {
    if (!this.isCreateMode) return;
    this.activeStep = 1;
    this.cdr.markForCheck();
  }

  private applyBusinessUnitRules(): void {
    const selectedBusinessUnitId = this.f['businessUnitId']?.value;
    if (selectedBusinessUnitId && this.businessUnitOptions.length === 0) {
      return;
    }

    const setControlState = (name: string, enabled: boolean, validators: any[] = []) => {
      const control = this.projectForm.get(name);
      if (!control) return;
      if (enabled) {
        control.enable({ emitEvent: false });
        control.setValidators(validators);
      } else {
        control.disable({ emitEvent: false });
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    };

    const skidInfoFields = [
      'transformerId', 'integratorId', 'startDate', 'deliveryDate', 'cxStartDate',
      'n1', 'n2', 'n3', 'n4', 'n5', 'n6', 'n7', 'skids',
      'auxPackage', 'oilContainment', 'auxiliaryRackLight', 'numberGfci',
      'incoTermsId', 'backfeedDate', 'freightBroker'
    ];

    const mode = this.getBusinessUnitMode();

    if (mode === 'GI') {
      this.clearGiResOnlyFields();
      setControlState('projectType', false);
      setControlState('inverterModelId', false);
      skidInfoFields.forEach((name) => setControlState(name, false));
      return;
    }

    setControlState('projectType', true, [Validators.required]);
    setControlState('inverterModelId', true, [Validators.required]);

    skidInfoFields.forEach((name) => {
      const validatorsByField: Record<string, any[]> = {
        startDate: [Validators.required],
        deliveryDate: [Validators.required],
        auxPackage: [Validators.required],
        oilContainment: [Validators.required],
        incoTermsId: [Validators.required],
        skids: [this.skidConfigurationValidator()]
      };
      setControlState(name, true, validatorsByField[name] || []);
    });

    // Ensure key RES fields are not left null after re-enabling controls.
    if (!this.f['projectType']?.value && this.projectTypeOptions.length > 0) {
      this.f['projectType']?.setValue(this.projectTypeOptions[0], { emitEvent: false });
    }
    if (!this.f['inverterModelId']?.value && this.inverterModelOptions.length > 0) {
      this.f['inverterModelId']?.setValue(this.inverterModelOptions[0]?.inverterModelId ?? null, { emitEvent: false });
    }
    if (!this.f['auxPackage']?.value && this.auxPackageOptions.length > 0) {
      this.f['auxPackage']?.setValue(this.auxPackageOptions[0], { emitEvent: false });
    }
    if (!this.f['oilContainment']?.value && this.oilContainmentOptions.length > 0) {
      this.f['oilContainment']?.setValue(this.oilContainmentOptions[0], { emitEvent: false });
    }
  }

  private clearGiResOnlyFields(): void {
    this.projectForm.patchValue({
      inverterModelId: null,
      transformerId: null,
      n1: null,
      n2: null,
      n3: null,
      n4: null,
      n5: null,
      n6: null,
      n7: null,
      skids: null
    }, { emitEvent: false });
  }

  getOracleProjectList() {
    // Manual trigger for project number validation (now handled via blur event)
    this.onProjectNumberBlur();
  }

  changeSkid() {
    setTimeout(() => {
      const skid = (+this.f['n1']?.value || 0) + (+this.f['n2']?.value || 0) + (+this.f['n3']?.value || 0) +
        (+this.f['n4']?.value || 0) + (+this.f['n5']?.value || 0) + (+this.f['n6']?.value || 0) + (+this.f['n7']?.value || 0);
      this.f['skids']?.setValue(skid, { emitEvent: false });

      // Mark skids field as touched to show validation errors
      this.f['skids']?.markAsTouched();

      // Trigger validation for skid configuration
      this.f['skids']?.updateValueAndValidity();
    });
  }

  onSubmit() {
    this.onSave();
  }

  onClose() {
    this.submitted = false;
    this.projectForm.reset();
    this.ref.close(this.createSuccessResult ?? undefined);
  }

  async onSave() {
    this.submitted = true;

    if (this.projectForm.invalid) {
      this.markFormGroupTouched(this.projectForm);
      return;
    }

    // if (!this.isProjectNumberVerified) {
    //   this.handleLoadingError('Please wait for project number verification to complete before creating the project.');
    //   return;
    // }

    if (this.isSaving) {
      return; // Prevent double submissions
    }

    this.isSaving = true;
    const projectData = this.projectForm.getRawValue() as ProjectModel;
    if (this.isGIBusinessUnit) {
      Object.assign(projectData, {
        inverterModelId: null,
        transformerId: null,
        n1: null,
        n2: null,
        n3: null,
        n4: null,
        n5: null,
        n6: null,
        n7: null,
        skids: null
      });
    }

    console.log('Project Form Data:', projectData);
    if (this.projectId > 0) {
      // Update existing project
      this._service.updateProject(projectData).pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSaving = false;
        })
      ).subscribe({
        next: (response) => {
          if (!response) {
            this.handleLoadingError('Failed to update project. Please try again.');
            return;
          }
          console.log('Project updated successfully:', response);
          this.ref.close({ success: true, data: projectData });
        }, error: (error) => {
          console.error('Error updating project:', error);
          this.handleLoadingError('Failed to update project. Please try again.');
        }
      });
    } else {
      this._service.createProject(projectData).pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSaving = false;
        })
      ).subscribe({
        next: (response) => {
          if (!response) {
            this.handleLoadingError('Failed to create project. Please try again.');
            return;
          }
          if (response == -1) {
            this.handleLoadingError('Project number already exists. Please use a different project number.');
            return;
          }
          console.log('Project created successfully:', response);
          projectData.projectId = response;
          this.createSuccessResult = { success: true, data: projectData };
          this.activeStep = 3;
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Project saved successfully.' });
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error creating project:', error);
          this.handleLoadingError('Failed to create project. Please try again.');
        }
      });
    }
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else {
        control?.markAsTouched();
      }
    });
  }

}
